import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiError,
  api,
  formatMoney,
  mineralTitle,
  type AiStatus,
  type Appraisal,
} from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { PhotoPicker, type PendingPhoto } from '../components/PhotoPicker.tsx';
import { SuggestField } from '../components/SuggestField.tsx';

/**
 * Оценка при покупке.
 *
 * Камень ещё чужой и стоит на прилавке, решение принимается за минуту.
 * Поэтому экран не имеет отношения к коллекции: ничего не сохраняется — ни
 * снимков, ни образца, ни оценки. Заводить карточку ради вопроса «что это
 * и сколько стоит» значит засорять каталог тем, что в итоге не купили.
 *
 * Цена показывается вилкой и никогда одним числом. На проверке из
 * четырнадцати образцов с известной ценой модель тянула всё к середине:
 * дешёвое завышала вдвое-втрое, дорогое занижала втрое и сильнее. В
 * середине диапазона оценка держится, и ровно об этом экран и говорит.
 */
export function AppraiseScreen() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [mineral, setMineral] = useState('');
  const [locality, setLocality] = useState('');
  const [size, setSize] = useState('');
  const [note, setNote] = useState('');
  const [asking, setAsking] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Appraisal | null>(null);

  useEffect(() => {
    void api
      .aiStatus()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  const mineralSuggestions = useCallback(
    (q: string) =>
      api.mineralSuggestions(q).then((rows) =>
        rows.map((r) => ({
          key: String(r.id),
          title: mineralTitle(r),
          rank: [r.formula, r.name_ru ? r.name : null].filter(Boolean).join(' · ') || undefined,
        })),
      ),
    [],
  );

  const localitySuggestions = useCallback(
    (q: string) =>
      api.localitySuggestions(q).then((rows) =>
        rows.map((r) => ({ key: String(r.id), title: r.name, rank: r.country ?? undefined })),
      ),
    [],
  );

  async function appraise() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      if (!status?.enabled) await api.updateProfile({ ai_enabled: true });
      const answer = await api.appraise(
        photos.map((photo) => photo.prepared.full),
        {
          mineral: mineral.trim() || null,
          locality_name: locality.trim() || null,
          size: size.trim() || null,
          note: note.trim() || null,
        },
      );
      setResult(answer);
      setStatus((s) => (s ? { ...s, enabled: true } : s));
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Не получилось оценить');
    } finally {
      setBusy(false);
    }
  }

  const askingNumber = Number(asking.replace(/[^\d]/g, ''));
  const verdict = result && askingNumber > 0 ? compare(askingNumber, result.price) : null;

  return (
    <div className="app">
      <div className="topbar topbar--bordered">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Назад">
          <Icon name="back" className="icon muted" />
        </button>
        <div className="spacer" />
        <h2 className="topbar-title">Оценка при покупке</h2>
        <div className="spacer" />
        <span style={{ width: 20 }} />
      </div>

      <div className="scroll">
        <p className="small muted" style={{ marginTop: 'var(--space-4)' }}>
          Ничего не сохраняется: ни снимки, ни оценка. Образец в коллекцию не
          заводится — это прикидка перед покупкой.
        </p>

        {status && !status.configured && (
          <div className="notice notice--warn" style={{ marginTop: 'var(--space-3)' }}>
            <Icon name="alert" className="icon icon-sm" />
            <span>Модель на сервере не настроена.</span>
          </div>
        )}

        <PhotoPicker photos={photos} onChange={setPhotos} />

        <div className="label-key">Минерал</div>
        <SuggestField
          value={mineral}
          onChange={setMineral}
          fetchSuggestions={mineralSuggestions}
          placeholder="если знаете"
        />
        <div className="hint">Оставьте пустым — определим по снимку.</div>

        <div className="label-key">Месторождение</div>
        <SuggestField
          value={locality}
          onChange={setLocality}
          fetchSuggestions={localitySuggestions}
          placeholder="если продавец назвал"
        />
        <div className="hint">
          Месторождение влияет на цену сильнее размера: тот же минерал с известной
          находки стоит в разы дороже.
        </div>

        <div className="label-key">Размер</div>
        <input
          className="field-input"
          value={size}
          placeholder="6×4×3 см"
          onChange={(e) => setSize(e.target.value)}
        />

        <div className="label-key">Что видите сами</div>
        <input
          className="field-input"
          value={note}
          placeholder="скол сзади, кристалл сколот с краю…"
          onChange={(e) => setNote(e.target.value)}
        />

        <div className="label-key">Сколько просят</div>
        <input
          className="field-input"
          inputMode="numeric"
          value={asking}
          placeholder="₽, необязательно"
          onChange={(e) => setAsking(e.target.value)}
        />

        <button
          className="btn btn--primary btn--full"
          style={{ marginTop: 'var(--space-4)' }}
          disabled={busy || !photos.length || !status?.configured}
          onClick={() => void appraise()}
        >
          {busy ? 'Считает…' : 'Оценить'}
        </button>
        {!photos.length && <div className="hint centered">Нужен хотя бы один снимок</div>}
        {busy && <div className="hint centered">Обычно это полминуты</div>}

        {error && (
          <p className="notice notice--danger" style={{ marginTop: 'var(--space-3)' }}>
            {error}
          </p>
        )}

        {result && (
          <div className="appraisal">
            <div className="label-key">Вилка</div>
            <div className="price-range">
              {formatMoney(result.price.low, 'RUB')} — {formatMoney(result.price.high, 'RUB')}
            </div>
            <div className="price-likely">
              вероятнее всего {formatMoney(result.price.likely, 'RUB')}
            </div>

            {verdict && (
              <div className={`notice notice--${verdict.tone}`} style={{ marginTop: 'var(--space-3)' }}>
                <Icon name={verdict.tone === 'ok' ? 'check' : 'alert'} className="icon icon-sm" />
                <span>{verdict.text}</span>
              </div>
            )}

            <div className="label-key">
              {result.mineral_guessed ? 'Определено по снимку' : 'Оценивали как'}
            </div>
            <div className="guess-name">{result.mineral}</div>
            {result.mineral_guessed && result.identification && (
              <div className="hint">
                Другие версии:{' '}
                {result.identification.candidates
                  .slice(1)
                  .map((c) => c.name_ru ?? c.name)
                  .join(', ') || 'нет'}
                . Если это не он — впишите название сами и спросите заново: от
                названия цена зависит сильнее всего.
              </div>
            )}

            {result.price.why && (
              <>
                <div className="label-key">На чём основано</div>
                <p className="notes">{result.price.why}</p>
              </>
            )}

            {result.price.caveat && (
              <div className="notice notice--warn" style={{ marginTop: 'var(--space-3)' }}>
                <Icon name="alert" className="icon icon-sm" />
                <span>{result.price.caveat}</span>
              </div>
            )}

            <p className="hint">
              Это прикидка, а не оценка коллекции, и в журнал оценок она не
              попадает. На проверке из четырнадцати образцов с известной ценой
              модель попала в вилку девять раз. {result.price.model}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Сравнение с ценой продавца считается здесь, а не моделью: это арифметика,
 * и платить за неё отдельным вопросом незачем.
 */
function compare(asking: number, price: { low: number; likely: number; high: number }) {
  if (asking > price.high) {
    const times = asking / Math.max(price.likely, 1);
    // На дорогих образцах оценка систематически занижена, и «просят выше
    // вилки» там означает не завышенную цену, а плохого судью. Вердикт
    // громче оговорки под ним, поэтому оговорка нужна прямо здесь.
    if (price.likely >= 30_000) {
      return {
        tone: 'warn' as const,
        text: `Просят выше вилки, но на образцах этого уровня оценка занижена втрое и сильнее — здесь она плохой судья. Смотрите на сам камень.`,
      };
    }
    return {
      tone: 'warn' as const,
      text: `Просят выше вилки — в ${times.toFixed(1)} раза дороже вероятной цены. Повод поторговаться или пройти мимо.`,
    };
  }
  if (asking < price.low) {
    return {
      tone: 'ok' as const,
      text: 'Просят ниже вилки. Либо удачная находка, либо с образцом что-то не так, чего не видно на снимке.',
    };
  }
  return { tone: 'ok' as const, text: 'Цена внутри вилки — обычная для такого образца.' };
}
